// src/components/layout/CustomHeader.tsx - Fixed mobile impersonation indicator
import React, { useState, useEffect } from 'react';
import { Layout, Space, Dropdown, Avatar, Typography, Tag, Button, message } from 'antd';
import { 
  UserOutlined, 
  CreditCardOutlined, 
  BarChartOutlined,
  LogoutOutlined,
  BellOutlined,
  ExportOutlined
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useGetIdentity, useLogout } from '@refinedev/core';
import { useNavigate } from 'react-router-dom';
import { getReviewLimit, getReviewsRemaining, hasActivePlan, getPlanDisplayName, getOverageCount } from '../../utils/planLimits';

const { Header } = Layout;

export const CustomHeader: React.FC = () => {
  const { data: user, refetch } = useGetIdentity<any>();
  const { mutate: logout } = useLogout();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Check for mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Keep refetching until we get user data
  useEffect(() => {
    if (!user && refetch) {
      console.log('User is null, refetching...');
      const interval = setInterval(() => {
        refetch();
      }, 1000);

      const timeout = setTimeout(() => {
        clearInterval(interval);
      }, 10000);

      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
      };
    } else if (user) {
      console.log('Header user data loaded:', user);
    }
  }, [user, refetch]);

  const handleMenuClick = ({ key }: { key: string }) => {
    switch (key) {
      case 'profile':
        navigate('/profile');
        break;
      case 'billing':
        navigate('/billing');
        break;
      case 'usage':
        navigate('/usage');
        break;
      case 'logout':
        logout();
        break;
    }
  };

  const menuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Account Settings',
    },
    {
      key: 'billing',
      icon: <CreditCardOutlined />,
      label: 'Billing & Plans',
    },
    {
      key: 'usage',
      icon: <BarChartOutlined />,
      label: 'Usage Statistics',
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      danger: true,
    },
  ];

  const getPlanColor = (plan: string) => {
    switch (plan) {
      case 'free': return 'default';
      case 'standard': return 'blue';
      case 'pro': return 'gold';
      default: return 'default';
    }
  };

  const needsSubscription = () => !hasActivePlan(user?.plan);

  const handleUpgradeClick = () => {
    navigate('/billing');
  };

  const handleExitImpersonation = () => {
    localStorage.removeItem('impersonateUid');
    message.success('Exited impersonation mode');
    window.location.reload();
  };

  return (
    <Header
      style={{
        backgroundColor: 'white',
        borderBottom: '1px solid #f0f0f0',
        padding: isMobile ? '0 16px' : '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '64px',
      }}
    >
      {/* Left side - Impersonation indicator on mobile */}
      <div style={{ 
        marginLeft: isMobile ? '56px' : '0',
        flex: isMobile ? 1 : 'none',
        overflow: 'hidden',
        marginRight: isMobile ? '8px' : '0',
        display: 'flex',
        alignItems: 'center'
      }}>
        {isMobile && user?.isImpersonated && (
          <Tag color="orange" style={{ 
            margin: 0, 
            fontSize: '12px',
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            Viewing as: {user.email}
          </Tag>
        )}
      </div>

      {/* Right side - User info and controls */}
      <Space size={isMobile ? 'small' : 'middle'} align="center">
        {/* Impersonation indicator on desktop */}
        {!isMobile && user?.isImpersonated && (
          <Tag color="orange" style={{ margin: 0 }}>
            Viewing as: {user.email}
          </Tag>
        )}
        
        {/* Plan tags - hide completely on mobile when impersonating */}
        {!(isMobile && user?.isImpersonated) && (
          <div style={{ 
            minWidth: isMobile ? 'auto' : '280px', 
            display: 'flex', 
            justifyContent: 'flex-end', 
            height: '32px', 
            alignItems: 'center',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none'
          }}>
            {user ? (
              <Space size="small">
                {!isMobile && (
                  <>
                    <Tag
                      color={getPlanColor(user.plan)}
                      style={{
                        margin: 0,
                        userSelect: 'none'
                      }}
                    >
                      {getPlanDisplayName(user.plan)}
                    </Tag>
                    {hasActivePlan(user.plan) && (
                      <Tag
                        color="blue"
                        style={{
                          margin: 0,
                          userSelect: 'none'
                        }}
                      >
                        {getOverageCount(user.plan, user.usage?.videosThisMonth || 0) > 0
                          ? `${getOverageCount(user.plan, user.usage?.videosThisMonth || 0)} over included`
                          : `${getReviewsRemaining(user.plan, user.usage?.videosThisMonth || 0)} of ${getReviewLimit(user.plan)} included left`}
                      </Tag>
                    )}
                  </>
                )}
                {needsSubscription() && !(isMobile && user?.isImpersonated) && (
                  <Tag 
                    color="warning" 
                    style={{ 
                      margin: 0,
                      cursor: 'pointer',
                      fontSize: isMobile ? '10px' : '11px',
                      fontWeight: 500,
                      userSelect: 'none',
                      transition: 'opacity 0.2s ease',
                    }}
                    onClick={handleUpgradeClick}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = '0.8';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = '1';
                    }}
                    title="Click to upgrade your plan"
                  >
                    {isMobile ? 'CHOOSE PLAN' : 'SUBSCRIBE TO CONTINUE'}
                  </Tag>
                )}
              </Space>
            ) : (
              // Loading state
              !isMobile && <Tag style={{ margin: 0 }}>Loading...</Tag>
            )}
          </div>
        )}

        {/* Notifications - hide on mobile if impersonating */}
        {!(isMobile && user?.isImpersonated) && (
          <Button
            type="text"
            shape="circle"
            size="middle"
            icon={<BellOutlined style={{ fontSize: 18, color: '#666' }} />}
            style={{ marginRight: 8 }}
            aria-label="Notifications"
          />
        )}

        {/* User Dropdown */}
        <Dropdown
          menu={{ items: menuItems, onClick: handleMenuClick }}
          placement="bottomRight"
          arrow
        >
          <Avatar 
            size="default" 
            style={{ 
              backgroundColor: user ? '#1890ff' : '#f0f0f0',
              cursor: 'pointer'
            }}
            icon={<UserOutlined />}
          >
            {user?.name?.charAt(0)?.toUpperCase()}
          </Avatar>
        </Dropdown>

        {/* Exit Impersonation Button - desktop only */}
        {!isMobile && user?.isImpersonated && (
          <Button
            danger
            icon={<ExportOutlined />}
            onClick={handleExitImpersonation}
            style={{ marginLeft: 8 }}
          >
            Exit User
          </Button>
        )}
      </Space>
    </Header>
  );
};